import { shortDateTime } from '../../shared/dateTime';

/**
 * Odpięcie auta przez planistę do wyjazdu na załadunek (odpowiedź
 * Marathonu/Euvic 16.10.2026, pyt. 6b; decyzja użytkownika 20.10.2026) —
 * front tylko pokazuje to, co policzyło API (`unassign-departure.ts`):
 * `unassignNeedsRequest` (czy auto już wyruszyło — wtedy wniosek)
 * i `departureEstimate` (chwila i na czym oparta). Reguły wyjazdu tu nie ma.
 *
 * Za przełącznikiem `unassignBeforeDeparture`: wyłączony — planista zawsze
 * składa wniosek, jak dotąd. Rozstrzyga zawsze API (403 po wyjeździe).
 */

/** Klucz przełącznika — jak w API (`feature-flags.ts`), nie przemianowujemy. */
export const FEATURE_UNASSIGN_BEFORE_DEPARTURE = 'unassignBeforeDeparture';

/** Ocena wyjazdu na załadunek z API — `basis` i daty to dane odpowiedzi. */
export interface DepartureEstimate {
  departed: boolean;
  /** Szacowana chwila wyjazdu (ISO); `null`, gdy rozstrzyga fakt z TMS albo trwające okno. */
  at: string | null;
  basis: string;
  /** Zdanie z API: skąd ta chwila. */
  reason: string;
}

/** Ocena wyjazdu z rekordu karty; `null`, gdy API jej nie podało (przełącznik, rola). */
export function departureEstimateOf(record: {
  [field: string]: unknown;
}): DepartureEstimate | null {
  const raw = record.departureEstimate as Partial<DepartureEstimate> | null | undefined;
  if (!raw || typeof raw !== 'object' || typeof raw.departed !== 'boolean') return null;
  return {
    departed: raw.departed,
    at: typeof raw.at === 'string' ? raw.at : null,
    basis: typeof raw.basis === 'string' ? raw.basis : '',
    reason: typeof raw.reason === 'string' ? raw.reason : '',
  };
}

/**
 * Czy planista odpina sam: przełącznik włączony i API mówi wprost „bez
 * wniosku” (`unassignNeedsRequest: false`). Brak pola — jak dotąd, wniosek.
 */
export function selfUnassignAllowed(
  record: { [field: string]: unknown },
  featureOn: boolean,
): boolean {
  return featureOn && record.unassignNeedsRequest === false;
}

/** „ (szac. 21.10, 06:15)” — przy szacunku; przy fakcie bez godziny pusto. */
function estimateSuffix(e: DepartureEstimate): string {
  return e.at ? ` (szac. ${shortDateTime(e.at)})` : '';
}

/** Przed wyjazdem — podpis przy „Odepnij”. */
export function beforeDepartureText(e: DepartureEstimate | null): string {
  if (!e?.at) return 'Do wyjazdu na załadunek odpinasz sam, bez wniosku';
  return `Do wyjazdu na załadunek${estimateSuffix(e)} odpinasz sam, bez wniosku`;
}

/**
 * Po wyjeździe — informacja przy wniosku o odpięcie. Bez danych do szacunku
 * API przyjmuje wyjazd zachowawczo (`loadingDayStart`), a bez daty
 * załadunku chwili nie zna (`noLoadingDate`) — tekst tego nie ukrywa.
 */
export function departedText(e: DepartureEstimate): string {
  const lead =
    e.basis === 'noLoadingDate'
      ? 'Nie wiadomo, kiedy auto wyrusza na załadunek'
      : e.basis === 'loadingDayStart'
        ? `Zachowawczo przyjmujemy, że auto wyruszyło na załadunek${estimateSuffix(e)}`
        : `Auto wyruszyło na załadunek${estimateSuffix(e)}`;
  return `${lead} — odpięcie zatwierdza przełożony`;
}
